<template>
  <div>
    <h5>Notifications</h5>
    <div class="table-card mb-3">
      <table class="responsive-table">
        <thead>
          <tr>
            <th>Notification</th>
            <th>Email</th>
            <th>Text</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(preference, index) in preferences" :key="index">
            <td>
              {{ preference.label }}
              <label class="d-block d-md-none"> Notification </label>
            </td>
            <td>
              <span id="disabled-wrapper" class="d-inline-block" tabindex="0">
                <b-form-checkbox
                  v-model="preference.canEmail"
                  :disabled="preference.isEmailRequired"
                  size="lg"
                  @change="setPreferenceAsModified(preference)"
                >
                </b-form-checkbox>
              </span>
              <b-tooltip v-if="preference.isEmailRequired" target="disabled-wrapper">This preference is required</b-tooltip>

              <label class="d-block d-md-none"> Email </label>
            </td>
            <td>
              <b-form-checkbox v-model="preference.canSMS" size="lg" @change="setPreferenceAsModified(preference)"></b-form-checkbox>
              <label class="d-block d-md-none"> Text </label>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="col-12 col-md-2 mt-2 p-0 text-right">
      <button class="btn btn-primary w-100" :disabled="!saveButtonEnabled || saving" @click="saveChanges()">
        <font-awesome-icon v-if="saving" :spin="true" :icon="['fas', 'spinner']" class="icon alt" />
        <span v-else>Save Changes</span>
      </button>
    </div>
  </div>
</template>

<script>
import UserClient from '@/api/UserClient';

export default {
    name: 'AccountPreferences',
    data() {
        return {
            userClient: new UserClient(this.$http),
            user:  this.$store.getters.currentUser,
            preferences: [],
            defaultPreferences: [{ communicationType: 'ToCounselorAboutSessionRequested', label: 'Session Requests', canEmail: true, isEmailRequired: true, canSMS: false }],
            saveButtonEnabled: false,
            saving: false
        }
    },
    async mounted() {
        await this.getCommuncationPreferences();
    },
    methods: {
        async getCommuncationPreferences() {
            try {
                const response = await this.userClient.getCommunicationPreferences(this.user.id);
                this.resetPreferencesFromResponse(response);
            } catch (ex) {
                this.alert('Failed to load preferences. Please try again!', true);
            }
        },
        async saveChanges() {
            const modifiedPreferences = this.getModifiedPreferences();
            const smsPreferences = modifiedPreferences.filter(x => x.canSMS);

            if (this.user.phone_number.length === 0 && smsPreferences.length > 0) {
              this.handleNoPhoneNumber();
              return;
            }

            try {
              this.saving = true;
                await this.userClient.setCommunicationPreferences(this.user.id, modifiedPreferences);
                this.alert('Preferences Saved Successfully!', false);
            } catch (ex) {
                this.alert('Failed to Update Preferences. Please try again!', true);
            } finally {
                this.saveButtonEnabled = false;
                this.saving = false;
            }
        },
        setPreferenceAsModified(preference) {
            const pref = this.preferences.find(p => p.communicationType === preference.communicationType);
            pref.isModified = true;
            this.saveButtonEnabled = true;
        },
        getModifiedPreferences() {
            const modifiedPreferences = [];
            this.preferences.forEach(preference => {
                if (preference.isModified) {
                    modifiedPreferences.push({
                        communicationType: preference.communicationType,
                        canEmail: preference.canEmail,
                        canSMS: preference.canSMS
                });
                }
            });
            return modifiedPreferences;
        },
        resetPreferencesFromResponse(response) {
            this.defaultPreferences.forEach(defaultPreference => {
                const preference = response.preferences.find(p => p.communicationType === defaultPreference.communicationType);
                this.preferences.push(preference ?
                            {
                                ...defaultPreference,
                                canEmail: preference.canEmail,
                                canSMS: this.user.phone_number.length === 0 ? false : preference.canSMS
                            } :
                            { ...defaultPreference });
            });
        },
        handleNoPhoneNumber() {
          this.alert('Please enter a valid cell phone number in the Personal tab', true);
              this.preferences.forEach(preference => {
                preference.canSMS = false;
              })
              this.saveButtonEnabled = false;
        },
        alert(message, e) {
            this.$store.commit('setGlobalAlert', {
                msg: message,
                error: e
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.table-card {
  @include border-radius($border-radius);

  border: solid 1px $border-color;
  margin-top: 30px;
  padding: 20px;
}

.responsive-table {
  @include border-radius($border-radius);


  width: 100%;
  border: none;

  thead {
    background: none;
    color: white;

    th {
      position: relative;
      padding: 0.75rem;
      padding-right: $gutter;
      white-space: nowrap;
      border-bottom: 2px solid rgba(255, 255, 255, 0.25);
      cursor: pointer;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      text-align: center;

      i,
      svg {
        position: absolute;
        top: 17px;
        right: 10px;
      }
      }
    }

  tbody {
    tr {
      td {
        padding: 0.75rem;
        color: rgba(white, 0.87);
        border-top: 1px solid rgba(255, 255, 255, 0.25);
        text-align: center;

        .custom-control-label::before,
.custom-control-label::after {
top: .8rem;
width: 1.25rem;
height: 1.25rem;
}

        a {
          color: darken($highlight-color, 5%);
        }
      }
    }
  }


  @media (max-width: $sm-max) {
    display: block;
    border: none;

    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    tbody {
      tr {
        background: $dark-blue;

        td {
          padding: 0.75rem;
          text-align: right;
          border-bottom: none;
          border-top: 1px solid rgba(255, 255, 255, 0.25);

          &:last-of-type {
            border-bottom: none;
          }
        }
      }
    }

    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }

    tr {
      @include border-radius($border-radius);

      margin: 0 0 1rem 0;
      background: white;
    }

    td {
      position: relative;
      padding-left: 50%;
      border: none;
      border-bottom: $secondary-border;

      label {
        position: absolute;
        top: 12.5px;
        left: 15px;
        width: 45%;
        padding-right: 10px;
        font-weight: bold;
        white-space: nowrap;
        text-align: left;
      }
    }
  }

}
</style>
